// @givmo/passkeys — authenticator-app setup's words on the web (AS-WEB14).
//
// From D-AS-21's approved frames A2 (set up), A3 (recovery codes) and A4 (the card's rows), approved
// word for word in the round-2 sweep (D-AS-35: D1, C7, D3), with fix 12's warning and turn-off
// confirmation. Two adaptations, adding no word: A2's line
// stops after its first sentence, since its second ("You'll type one in when you sign a board
// document.") is Board's context and the console's card is not (the warning under the code says what
// a code is asked for here); and A4's "Off" row draws only for an account the web offers setup to.
//
// The lines the frames do not carry are the apps' where the apps have one (the replace confirmation,
// C8; the recovery-code mint failures, C10; iOS's step-up title for replacing codes), with the web's
// place name, and the sheet bodies of D-AS-35 fix 9. All approved (D-AS-35).

import { FACTOR_RESET_PLACE } from './factor-reset-copy';

export const AUTHENTICATOR_WORDS = {
  // A2
  setUpTitle: 'Set up an authenticator app',
  setUpLine: 'An authenticator app on your phone shows a new 6-digit code every 30 seconds.',
  stepAdd: 'Add Givmo to the app',
  scanLine: 'In your authenticator app, add an account and scan this code.',
  cantScan: 'Can’t scan? Enter this key instead',
  qrLabel: 'QR code that adds Givmo to your authenticator app',
  openInApp: 'Open in your authenticator app',
  typeKey: 'Or type this key into the app:',
  copy: 'Copy',
  stepCode: 'Enter the 6-digit code it shows',
  codeLabel: '6-digit code',
  // D-AS-35 fix 12: a passkey sign-in never asks for the code.
  warning:
    'Once it’s on, Givmo asks for a code from the app each time you sign in, unless you sign in with a passkey. If you use the Givmo app, update it first: older versions can’t ask for the code, so they won’t sign you in.',
  turnOn: 'Turn on',
  back: 'Back',
  // The sheet's approved line for a refused code (B2d-fail).
  codeRefused: 'That code didn’t work. Enter the code your app shows now.',
  // A3
  codesTitle: 'Save your recovery codes',
  codesLine:
    'If you lose your phone, a recovery code gets you back into your account. Each code works once.',
  copyAll: 'Copy all',
  download: 'Download',
  codesNote:
    'Keep them somewhere safe, like your password manager or a printed page. This is the only time we can show them.',
  saved: 'I’ve saved these',
  // A4
  rowTitle: 'Authenticator app',
  off: 'Off',
  setUp: 'Set up',
  turnOff: 'Turn off',
  codesRowTitle: 'Recovery codes',
  newCodes: 'Get new codes',
  // D-AS-35 fix 12: the row this opens from is "Authenticator app", and an account with passkeys
  // keeps a second step once it's off, so it names the app, not two-factor authentication.
  turnOffTitle: 'Turn off your authenticator app?',
  turnOffLine: 'Your recovery codes will stop working. You can set it up again at any time.',
  // iOS's confirmation for replacing the codes (approved, D-AS-35 C8).
  replaceTitle: 'Replace your recovery codes?',
  replaceLine:
    'Every code you have now stops working, including any you haven’t used. We’ll show the new set once — this is the only time you can read them.',
  replace: 'Replace',
  cancel: 'Cancel',
  // iOS's mint failures, with the web's place name (approved, D-AS-35 C10).
  codesUnavailable: `Two-factor authentication is on, but we couldn’t create your recovery codes. Create them from ${FACTOR_RESET_PLACE} — without recovery codes, losing your authenticator app means waiting out an email-verified reset.`,
  codesAlreadyIssued: `Two-factor authentication is on. You already have unused recovery codes from before — keep using those, or create a new set from ${FACTOR_RESET_PLACE}.`,
  failed: 'That didn’t work. Try again.',
} as const;

/** The sheet's rows for the authenticator acts (`@givmo/step-up` copy-table keys). */
export const AUTHENTICATOR_STEP_UP_ROWS = {
  setUp: 'authenticator_set_up',
  turnOff: 'authenticator_turn_off',
  /** The backend's own action name on the regenerate refusal (`context.action`). */
  replaceCodes: 'regenerate_recovery_codes',
} as const;

/** "On since Oct 6, 2026", "10 left · Created Oct 6, 2026": A4's details. */
const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function parsed(value: string | null | undefined): Date | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The authenticator row's detail: "On since {date}", or "On" when the date is unknown. */
export function authenticatorOnDetail(enrolledAt: string | null | undefined): string {
  const date = parsed(enrolledAt);
  return date === null ? 'On' : `On since ${DATE.format(date)}`;
}

/** The recovery-codes row's detail: "10 left · Created Oct 6, 2026". */
export function recoveryCodesDetail(
  remaining: number,
  generatedAt: string | null | undefined,
): string {
  const date = parsed(generatedAt);
  return date === null ? `${remaining} left` : `${remaining} left · Created ${DATE.format(date)}`;
}

/** The key as the frames print it: groups of four ("JBSW Y3DP EHPK 3PXP"). */
export function groupedSecretKey(secretKey: string): string {
  return (secretKey.match(/.{1,4}/g) ?? []).join(' ');
}
